// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { EcosystemMetricType, EcosystemInfluenceScoreData, MetricCardStatusType, MetricBottomSummary, ChartDataWithMetricType, EcosystemMetricValues, ChartData } from '@lfx/key-projects/key-projects.model';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import { BoardMembers } from './board-members.entity';
import { CommitteeMembers } from './committee-members.entity';
import { MeetingAttendance } from './meeting-attendance.entity';
import { EventAttendance } from './event-attendance.entity';
import { EventSpeakers } from './event-speakers.entity';
import { EventSponsorships } from './event-sponsorships.entity';
import { MeetupAttendance } from './meetup-attendance.entity';
import { CollaborationActivity } from './collaboration-activity.entity';
import { CertifiedIndividuals } from './certified-individuals.entity';
import { ScorePoints, ChartType, BaseMetricConfig, ScoreLevel, SCORE_MAPPING, BaseMetricEntity, ScoreLevelStyles, LEVEL_STYLE_MAPPING } from './base-metric.entity';

interface MetricEntityClass {
  METRIC_TYPE: string;
  getDisplayConfig(): BaseMetricConfig['display'];
  getConfig(): BaseMetricConfig;
  getBottomSummary(value: number | string, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null, points?: number): MetricBottomSummary;
  getIconTypeForChartData(chartData: {metricCount: number; othersCount: number}[]): 'eyes' | undefined;
  transformToCardData?(
    metric: ChartDataWithMetricType,
    metricValues: EcosystemMetricValues | undefined,
    isLfProject: boolean,
    projectType: string | null | undefined,
    projectName: string | null | undefined,
    foundationName: string | null | undefined
  ): EcosystemMetricCardData;
}

const METRIC_ENTITY_MAPPING: Record<string, MetricEntityClass> = {
  [BoardMembers.METRIC_TYPE]: BoardMembers,
  [CommitteeMembers.METRIC_TYPE]: CommitteeMembers,
  [MeetingAttendance.METRIC_TYPE]: MeetingAttendance,
  [EventAttendance.METRIC_TYPE]: EventAttendance,
  [EventSpeakers.METRIC_TYPE]: EventSpeakers,
  [EventSponsorships.METRIC_TYPE]: EventSponsorships,
  [MeetupAttendance.METRIC_TYPE]: MeetupAttendance,
  [CollaborationActivity.METRIC_TYPE]: CollaborationActivity,
  [CertifiedIndividuals.METRIC_TYPE]: CertifiedIndividuals
};

export interface EcosystemMetricCardData {
  title: string;
  titleDescription: string;
  chartType: ChartType;
  firstDate: string;
  lastDate: string;
  bottomSummary?: MetricBottomSummary;
  metricType: EcosystemMetricType;
  data?: number[];
  multiSeriesData?: {
    data: number[];
    label?: string;
    dashArray?: number[];
  }[];
  xAxisLabels?: string[];
  iconType?: 'eyes' | 'no-signal' | undefined;
  header?: string;
  headerVariant?: 'neutral' | 'accent';
  // Sorting properties
  isProjectLevelChart?: boolean;
  isFoundationLevelChart?: boolean;
  companyHasActivity?: boolean;
  projectHasActivity?: boolean;
  projectChoosesNotToUseTool?: boolean;
}

export interface EcosystemInfluenceComponentConfig {
  titleText: string;
  influenceType: string;
  noDataDescription: string;
  loadingDescription: string;
  loadingStatusType: MetricCardStatusType;
  loadingChartType: ChartType;
  multiSeriesData: {
    ourOrganization: {
      label: string;
    };
    others: {
      label: string;
      dashArray: number[];
    };
  };
}

export interface EcosystemMetricScoreResult {
  points: ScorePoints;
  description: string;
}

export type EcosystemScoreLevel = ScoreLevel | 'Non-LF Project';

export class EcosystemInfluence {
  static readonly COMPONENT_CONFIG: EcosystemInfluenceComponentConfig = {
    titleText: 'Our Ecosystem Influence',
    influenceType: 'ecosystem',
    noDataDescription: 'This metric does not apply to this project because this project has no data of this type.',
    loadingDescription: 'Loading...',
    loadingStatusType: 'info',
    loadingChartType: 'loading' as ChartType,
    multiSeriesData: {
      ourOrganization: {
        label: 'Our Organization'
      },
      others: {
        label: 'Others',
        dashArray: [2, 2]
      }
    }
  };

  private static determineNeverUsedLfxTools(
    allTimeTotalForMetric: number
  ): boolean {
    return allTimeTotalForMetric === 0;
  }

  /**
   * Metrics that don't depend on LF membership status.
   * These are project-level metrics that can have data for any project,
   * regardless of LF affiliation:
   * - collaboration_activity: Data from GitHub/GitLab repositories
   * - meeting_attendance: Project meeting data
   */
  private static readonly PROJECT_LEVEL_METRICS: EcosystemMetricType[] = ['collaboration_activity', 'meeting_attendance'];

  private static calculateGenericIconType(
    neverUsedLfxTools: boolean,
    isLfProject: boolean,
    chartData: ChartData[] | undefined,
    entityClass: MetricEntityClass | undefined,
    metricType?: EcosystemMetricType
  ): 'eyes' | 'no-signal' | undefined {
    // Project-level metrics (like collaboration_activity) don't depend on LF membership
    // because they get data from code/documentation contributions on project repositories.
    const isProjectLevelMetric = metricType && this.PROJECT_LEVEL_METRICS.includes(metricType);

    // Show 'no-signal' icon when:
    // 1. Project never used LFX tools for this activity type (allTimeTotal === 0), OR
    // 2. Project is not an LF project AND the metric requires LF membership
    if (neverUsedLfxTools || (!isProjectLevelMetric && isLfProject === false)) {
      return 'no-signal';
    }

    if (chartData && entityClass) {
      return entityClass.getIconTypeForChartData(chartData);
    }

    return undefined;
  }

  private static buildGenericMultiSeriesData(
    chartData: ChartData[]
  ): { data: number[]; label?: string; dashArray?: number[] }[] {
    return [
      {
        data: chartData.map(d => d.metricCount),
        label: 'Our Organization'
      },
      {
        data: chartData.map(d => d.othersCount),
        dashArray: [2, 2],
        label: 'Others'
      }
    ];
  }

  /**
   * Extract all metric-specific data from metricValues in a single pass
   *
   * Centralizes metric data extraction to avoid multiple property accesses.
   * Each metric has three key properties:
   * - value: Current metric value (count or percentage)
   * - total: All-time cumulative total for the metric
   * - level: Pre-calculated influence level (Silent/Participating/Contributing/Leading)
   *
   * @param metricValues - The complete ecosystem metric values object
   * @param metricType - The specific metric type to extract data for
   * @returns Object containing metricValue, allTimeTotal, and level for the given metric type
   * @throws Error if metricType is invalid
   */
  private static extractMetricData(
    metricValues: EcosystemMetricValues,
    metricType: EcosystemMetricType
  ): { metricValue: number | string | undefined; allTimeTotal: number; level: string | undefined; points?: number } {
    // Map each metric type to its corresponding properties in metricValues
    // This mapping is derived from the database schema and API response structure
    const metricDataMapping: Record<EcosystemMetricType, {
      value: number | string | undefined;
      total: number;
      level: string | undefined;
      points?: number;
    }> = {
      'board_members': {
        value: metricValues?.boardMembersCount,
        total: metricValues?.allTimeTotalBoardMembers ?? 0,
        level: metricValues?.boardMembersLevel
      },
      'committee_members': {
        value: metricValues?.committeeMembersPercent,
        total: metricValues?.allTimeTotalCommitteeMembers ?? 0,
        level: metricValues?.committeeMembersLevel
      },
      'meeting_attendance': {
        value: metricValues?.meetingAttendancePercent,
        total: metricValues?.allTimeTotalMeetingAttendance ?? 0,
        level: metricValues?.meetingAttendanceLevel
      },
      'event_attendance': {
        value: metricValues?.eventAttendancePercent,
        total: metricValues?.allTimeTotalEventAttendance ?? 0,
        level: metricValues?.eventAttendanceLevel
      },
      'event_speakers': {
        value: metricValues?.eventSpeakersPercent,
        total: metricValues?.allTimeTotalEventSpeakers ?? 0,
        level: metricValues?.eventSpeakersLevel
      },
      'event_sponsorships': {
        value: metricValues?.sponsorshipEventsPercent,
        total: metricValues?.allTimeTotalSponsorshipEvents ?? 0,
        level: metricValues?.sponsorshipEventsLevel
      },
      'meetup_attendance': {
        value: metricValues?.meetupAttendancePercent,
        total: metricValues?.allTimeTotalMeetupAttendance ?? 0,
        level: metricValues?.meetupAttendanceLevel
      },
      'collaboration_activity': {
        value: metricValues?.collaborationActivityPercent,
        total: metricValues?.allTimeTotalCollaborationActivity ?? 0,
        level: metricValues?.collaborationActivityLevel,
        points: metricValues?.collaborationActivityPoints
      },
      'certified_individuals': {
        value: metricValues?.tncCertificatePercent,
        total: metricValues?.allTimeTotalTncCertificate ?? 0,
        level: metricValues?.tncCertificateLevel
      }
    };

    const data = metricDataMapping[metricType];
    if (!data) {
      throw new Error(`Invalid metric type: ${metricType}. Cannot extract metric data.`);
    }

    return {
      metricValue: data.value,
      allTimeTotal: data.total,
      level: data.level,
      points: data.points
    };
  }

  /**
   * Build the base card configuration without chart-specific data
   */
  private static buildBaseCard(
    config: BaseMetricConfig,
    metric: ChartDataWithMetricType,
    metricType: EcosystemMetricType,
    hasData: boolean,
    bottomSummary: MetricBottomSummary,
    iconType: 'eyes' | 'no-signal' | undefined,
    header?: string,
    headerVariant?: 'neutral' | 'accent'
  ): Omit<EcosystemMetricCardData, 'data' | 'multiSeriesData' | 'xAxisLabels'> {
    const actualChartType = hasData ? config.display.chartType : 'no-data';

    return {
      title: config.display.displayName,
      titleDescription: BaseMetricEntity.generateRulesHtml(config.display.tooltip),
      chartType: actualChartType,
      firstDate: metric.firstDate,
      lastDate: metric.lastDate,
      bottomSummary,
      metricType,
      iconType,
      header,
      headerVariant
    };
  }

  /**
   * Transform metric data to card data format
   *
   * Delegates to entity-specific transformer if available, otherwise uses generic transformation.
   * Handles both data and no-data states appropriately.
   *
   * @throws {Error} If metric type is invalid or configuration cannot be retrieved
   */
  static transformToCardData(
    metric: ChartDataWithMetricType,
    metricValues: EcosystemMetricValues,
    isLfProject?: boolean,
    projectName?: string | null,
    foundationName?: string | null,
    projectType?: string | null
  ): EcosystemMetricCardData {
    const metricType = metric.metricType as EcosystemMetricType;

    // Delegate to entity-specific transformer if available
    const entityClass = METRIC_ENTITY_MAPPING[metricType];
    if (entityClass?.transformToCardData) {
      return entityClass.transformToCardData(metric, metricValues, isLfProject, projectType, projectName, foundationName);
    }

    // Generic transformation
    const config = this.getConfig(metricType);
    const hasData = metric.chartData && metric.chartData.length > 0;
    const { header, headerVariant } = this.getMetricHeader(metricType, projectType, projectName, foundationName);

    const isProjectLevelChart = metricType === 'meeting_attendance' || metricType === 'collaboration_activity';
    const isFoundationLevelChart = !isProjectLevelChart;

    // Extract metric-specific data
    const { metricValue, allTimeTotal, level, points } = this.extractMetricData(metricValues, metricType);

    // Calculate summary and icon
    const bottomSummary = this.getBottomSummary(metricType, metricValue, allTimeTotal, isLfProject, level as ScoreLevel, projectType, points);
    const neverUsedLfxTools = this.determineNeverUsedLfxTools(allTimeTotal);
    const iconType = this.calculateGenericIconType(neverUsedLfxTools, isLfProject, metric.chartData, entityClass, metricType);

    // Build base card
    const baseCard = this.buildBaseCard(config, metric, metricType, hasData, bottomSummary, iconType, header, headerVariant);

    // Return early for no-data case
    if (!hasData) {
      return {
        ...baseCard,
        data: [],
        multiSeriesData: [],
        xAxisLabels: [],
        isProjectLevelChart,
        isFoundationLevelChart,
        companyHasActivity: false,
        projectHasActivity: false,
        projectChoosesNotToUseTool: allTimeTotal === 0
      };
    }

    // Calculate cumulative counts for activity detection
    const cumulativeMetricCount = metric.chartData.reduce((acc, curr) => acc + curr.metricCount, 0);
    const cumulativeOthersCount = metric.chartData.reduce((acc, curr) => acc + curr.othersCount, 0);
    const companyHasActivity = cumulativeMetricCount > 0;
    const projectHasActivity = cumulativeMetricCount > 0 || cumulativeOthersCount > 0;
    const projectChoosesNotToUseTool = allTimeTotal === 0;

    // Build chart data
    const xAxisLabels = KeyProjectsHelper.generateXAxisLabels(metric.chartData);
    const multiSeriesData = this.buildGenericMultiSeriesData(metric.chartData);


    return {
      ...baseCard,
      data: metric.chartData.map(d => d.metricCount),
      xAxisLabels,
      multiSeriesData,
      isProjectLevelChart,
      isFoundationLevelChart,
      companyHasActivity,
      projectHasActivity,
      projectChoosesNotToUseTool
    };
  }

  /**
   * Determine header text and variant for a metric card based on metric type and project type
   *
   * Business rules:
   * - Foundation type: All metrics show foundation name with neutral variant
   * - Project type:
   *   - Project-level metrics (meeting_attendance, collaboration_activity): Show "[Project Name] Project" with accent variant
   *   - Foundation-level metrics: Show foundation name with neutral variant
   *
   * Visual hierarchy:
   * - Accent variant (colored): Used for project-specific metrics to highlight project-level data
   * - Neutral variant (gray): Used for foundation-level metrics shared across multiple projects
   */
  private static getMetricHeader(
    metricType: EcosystemMetricType,
    projectType?: string | null,
    projectName?: string | null,
    foundationName?: string | null
  ): { header?: string; headerVariant?: 'neutral' | 'accent' } {
    // If no names provided, no header is shown
    if (!projectName && !foundationName) {
      return {};
    }

    // Normalize projectType to lowercase for consistent comparison
    const normalizedProjectType = projectType?.toLowerCase();

    // Foundation type: all metrics show foundation name with neutral variant
    if (normalizedProjectType === 'foundation') {
      return {
        header: foundationName || undefined,
        headerVariant: 'neutral'
      };
    }

    // Project type: check metric type
    const projectLevelMetrics: EcosystemMetricType[] = ['meeting_attendance', 'collaboration_activity'];

    if (projectLevelMetrics.includes(metricType)) {
      return {
        header: projectName ? `${projectName} Project` : undefined,
        headerVariant: 'accent'
      };
    }

    // Foundation-level metrics in project view - use neutral variant
    return {
      header: foundationName || undefined,
      headerVariant: 'neutral'
    };
  }

  static getConfig(metricType: EcosystemMetricType): BaseMetricConfig {
    const entityClass = METRIC_ENTITY_MAPPING[metricType];
    if (!entityClass) {
      throw new Error(`No entity class found for metric type: ${metricType}`);
    }
    return entityClass.getConfig();
  }

  static getBottomSummary(metricType: EcosystemMetricType, value: string | number, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null, points?: number): MetricBottomSummary {
    const entityClass = METRIC_ENTITY_MAPPING[metricType];
    if (!entityClass) {
      throw new Error(`No entity class found for metric type: ${metricType}`);
    }
    return entityClass.getBottomSummary(value, allTimeTotal, isLfProject, level, projectType, points);
  }

  /**
   * Create skeleton cards for loading state
   */
  static createSkeletonCards(): EcosystemMetricCardData[] {
    const ecosystemMetrics = this.getEcosystemMetricTypes();
    const styles = BaseMetricEntity.getNullValueStyles();

    return ecosystemMetrics.map(metricType => {
      const entityClass = METRIC_ENTITY_MAPPING[metricType];
      const displayConfig = entityClass ? entityClass.getDisplayConfig() : { displayName: metricType, chartType: 'loading' as ChartType };

      return {
        title: displayConfig.displayName,
        titleDescription: this.COMPONENT_CONFIG.loadingDescription,
        chartType: this.COMPONENT_CONFIG.loadingChartType,
        firstDate: '',
        lastDate: '',
        metricType: metricType,
        data: [],
        multiSeriesData: [],
        xAxisLabels: [],
        bottomSummary: {
          title: '',
          description: this.COMPONENT_CONFIG.loadingDescription,
          icon: styles.icon,
          iconColor: styles.iconColor,
          borderColor: styles.borderColor
        }
      };
    });
  }

  /**
   * Get all ecosystem metric types
   */
  static getEcosystemMetricTypes(): readonly EcosystemMetricType[] {
    return [
      'board_members',
      'committee_members',
      'meeting_attendance',
      'event_attendance',
      'event_speakers',
      'event_sponsorships',
      'meetup_attendance',
      'collaboration_activity',
      'certified_individuals'
    ] as const;
  }

  /**
   * Get pre-calculated ecosystem influence score points from metric values
   *
   * ALL VALUES PRE-CALCULATED IN SNOWFLAKE - no calculation logic here.
   * Simply reads the pre-calculated influence level from the database.
   *
   * Influence levels (pre-calculated by dbt models with dynamic threshold adjustment):
   * - 0 => Silent
   * - 1 => Participating
   * - 2 => Contributing
   * - 3 => Leading
   */
  static getOverallScorePoints(metricValues: EcosystemMetricValues, isLfProject?: boolean): ScorePoints {
    // If project is not an LF project, return 0 points
    if (isLfProject === false) {
      return 0;
    }

    // Use pre-calculated influence level from database (0-3)
    // All scoring logic including dynamic threshold adjustment is done in dbt
    return metricValues.influenceLevel as ScorePoints;
  }

  /**
   * Get overall ecosystem influence score level from metric values
   * Uses SCORE_MAPPING to convert ScorePoints to ScoreLevel
   * @param metricValues - The ecosystem metric values to calculate score level from
   * @param isLfProject - Whether the project is an LF project
   * @returns EcosystemScoreLevel ('Silent' | 'Participating' | 'Contributing' | 'Leading' | 'Non-LF Project')
   */
  static getOverallScoreLevel(metricValues: EcosystemMetricValues, isLfProject?: boolean): EcosystemScoreLevel {
    // If project is not an LF project, return special status
    if (isLfProject === false) {
      return 'Non-LF Project';
    }

    const scorePoints = this.getOverallScorePoints(metricValues, isLfProject);
    return SCORE_MAPPING[scorePoints];
  }

  /**
   * Get overall score level styles from metric values
   * @param metricValues - The ecosystem metric values to calculate score level styles from
   * @param isLfProject - Whether the project is an LF project
   * @returns ScoreLevelStyles
   */
  static getOverallScoreLevelStyles(metricValues: EcosystemMetricValues, isLfProject?: boolean): ScoreLevelStyles {
    const scoreLevel = this.getOverallScoreLevel(metricValues, isLfProject);

    // Handle special case for Non-LF Project
    if (scoreLevel === 'Non-LF Project') {
      // Return a neutral style for Non-LF projects
      return BaseMetricEntity.getNullValueStyles();
    }

    return LEVEL_STYLE_MAPPING[scoreLevel as ScoreLevel];
  }

  /**
   * Calculate ecosystem influence score from individual metric scores
   * @deprecated Use getOverallScorePoints instead
   */
  static calculateInfluenceScore(scoreData: EcosystemInfluenceScoreData): EcosystemMetricScoreResult {
    if (!scoreData) {
      return {
        points: 0,
        description: 'No score data available'
      };
    }

    // If project is not an LF project, return Non-LF Project status
    if (scoreData.isLfProject === false) {
      return {
        points: 0,
        description: 'This project is not a Linux Foundation project'
      };
    }

    // Map Snowflake level (1-4) to our score points (0-3)
    const points = Math.max(0, Math.min(3, scoreData.level - 1)) as ScorePoints;

    return {
      points,
      description: scoreData.category || 'Ecosystem influence level'
    };
  }
}
